/*
  Saturn Los Angeles CRO rebuild. One small deferred file for everything the rebuild adds
  on top of Craft: sticky add-to-bag, gallery dots, description clamp, size-chart jump,
  free-shipping bars, bundle add, cart-drawer quick add and the SHOW MORE grid loader.
  Depends on constants.js / pubsub.js / global.js (publish, subscribe, fetchConfig, routes).
*/
(() => {
  const money = (cents) => {
    const value = cents / 100;
    return '$' + (Number.isInteger(value) ? value : value.toFixed(2));
  };

  /* ---- Free-shipping bars: keep PDP and drawer bars in sync with the cart ---- */
  function paintShipbars(totalCents) {
    document.querySelectorAll('[data-sat-shipbar]').forEach((bar) => {
      const threshold = Number(bar.dataset.threshold) || 0;
      if (!threshold) return;
      const running = totalCents + (Number(bar.dataset.extra) || 0);
      const remaining = threshold - running;
      const pct = Math.max(0, Math.min(100, Math.round((running / threshold) * 100)));
      const fill = bar.querySelector('[data-sat-shipbar-fill]');
      const text = bar.querySelector('[data-sat-shipbar-text]');
      if (fill) fill.style.width = pct + '%';
      bar.querySelector('.sat-shipbar__track')?.setAttribute('aria-valuenow', pct);
      if (!text) return;
      if (remaining <= 0) {
        bar.classList.add('is-unlocked');
        text.textContent = bar.dataset.textUnlocked || '';
      } else {
        bar.classList.remove('is-unlocked');
        text.innerHTML = (bar.dataset.textRemaining || '').replace('[amount]', money(remaining));
      }
    });
  }
  function refreshShipbars() {
    fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => paintShipbars(cart.total_price))
      .catch(() => {});
  }
  /* constants.js declares PUB_SUB_EVENTS with a top-level const, so it is not on window;
     typeof is the only safe check. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(refreshShipbars, 50));
  }

  /* ---- Add to cart helper used by bundle + drawer quick add ---- */
  async function addItems(items, button, messageEl) {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    if (messageEl) messageEl.hidden = true;
    try {
      const res = await fetch(window.routes.cart_add_url, { ...fetchConfig('javascript'), body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status) {
        if (messageEl) {
          messageEl.textContent = data.description || data.message || 'Could not add that. Try again.';
          messageEl.hidden = false;
        }
        return false;
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'saturn-add', cartData: data });
      if (drawer) {
        /* Craft's product form clears this; without it the drawer keeps its empty-state
           layout (no header, collapsed items) after the first quick add. */
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location.href = window.routes.cart_url;
      }
      return true;
    } catch (e) {
      if (messageEl) {
        messageEl.textContent = 'Could not add that. Try again.';
        messageEl.hidden = false;
      }
      return false;
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  }

  document.addEventListener('click', (event) => {
    /* Drawer upsell pills */
    const quick = event.target.closest('[data-sat-quick-add]');
    if (quick) {
      event.preventDefault();
      if (quick.disabled || quick.getAttribute('aria-disabled') === 'true') return;
      addItems([{ id: Number(quick.dataset.satQuickAdd), quantity: 1 }], quick, quick.closest('.sat-upsell')?.querySelector('[data-sat-upsell-msg]'));
      return;
    }

    /* Complete the Fit bundle */
    const bundleBtn = event.target.closest('[data-sat-bundle-add]');
    if (bundleBtn) {
      event.preventDefault();
      if (bundleBtn.getAttribute('aria-disabled') === 'true') return;
      const wrap = bundleBtn.closest('[data-sat-bundle]');
      const msg = wrap?.querySelector('[data-sat-bundle-msg]');
      const form = document.getElementById(bundleBtn.dataset.form);
      const mainInput = form?.querySelector('[name="id"]');
      const companion = wrap?.querySelector('[data-sat-bundle-companion]');
      if (!mainInput || mainInput.disabled || !mainInput.value) {
        if (msg) {
          msg.textContent = 'Pick an available size above first.';
          msg.hidden = false;
        }
        return;
      }
      const run = async () => {
        const code = wrap?.dataset.discountCode;
        if (code) {
          try {
            await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
          } catch (e) {
            /* the discount is a nicety; the add still goes ahead */
          }
        }
        addItems(
          [
            { id: Number(companion.value), quantity: 1 },
            { id: Number(mainInput.value), quantity: 1 },
          ],
          bundleBtn,
          msg
        );
      };
      run();
      return;
    }

    /* "Size chart" link opens the size guide as a modal dialog */
    const chartOpen = event.target.closest('[data-sat-open-sizechart]');
    if (chartOpen) {
      event.preventDefault();
      const dlg = document.getElementById(chartOpen.getAttribute('data-sat-open-sizechart'));
      if (dlg && dlg.showModal) { dlg.showModal(); }
      else if (dlg) { dlg.setAttribute('open', ''); }
      return;
    }
    const chartClose = event.target.closest('[data-sat-close-sizechart]');
    if (chartClose) { chartClose.closest('dialog')?.close(); return; }
    /* click on the dialog backdrop (the dialog element itself) closes it */
    if (event.target.matches('dialog.sat-sizechart-dialog')) { event.target.close(); return; }

    /* "Size chart" link opens the size guide accordion */
    const jump = event.target.closest('[data-sat-open-accordion]');
    if (jump) {
      const id = (jump.getAttribute('href') || '').replace(/^#/, '');
      const box = document.getElementById(id);
      if (!box) return;
      event.preventDefault();
      const details = box.querySelector('details') || box.closest('details');
      if (details) details.open = true;
      box.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* ---- Sticky add-to-bag (mobile) ---- */
  function initStickyAtc() {
    document.querySelectorAll('[data-sat-sticky-atc]').forEach((bar) => {
      if (bar.dataset.ready) return;
      const target = document.getElementById(bar.dataset.target);
      if (!target) return;
      bar.dataset.ready = '1';
      const button = bar.querySelector('[data-sat-sticky-button]');
      const label = button.querySelector('span');
      const priceEl = bar.querySelector('[data-sat-sticky-price]');
      const priceSource = () => {
        const box = document.getElementById(`price-${bar.dataset.section}`);
        if (!box) return null;
        const sale = box.querySelector('.price--on-sale .price-item--sale');
        return (sale || box.querySelector('.price-item--regular'))?.textContent.trim() || null;
      };
      const sync = () => {
        label.textContent = (target.querySelector('span') || target).textContent.trim();
        button.toggleAttribute('disabled', target.hasAttribute('disabled'));
        const p = priceSource();
        if (p && priceEl) priceEl.textContent = p;
      };
      new MutationObserver(sync).observe(target, { attributes: true, childList: true, subtree: true, characterData: true });
      const priceBox = document.getElementById(`price-${bar.dataset.section}`);
      if (priceBox) new MutationObserver(sync).observe(priceBox, { childList: true, subtree: true, characterData: true });
      sync();
      bar.removeAttribute('hidden');
      /* A scroll check rather than an IntersectionObserver: the observer only reports
         transitions, so a fast flick from above the button to below it never fires. */
      let ticking = false;
      const check = () => {
        ticking = false;
        const show = target.getBoundingClientRect().bottom < 0 && window.innerWidth < 750;
        bar.classList.toggle('is-visible', show);
        document.body.classList.toggle('sat-sticky-visible', show);
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(check);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
      button.addEventListener('click', () => {
        if (target.hasAttribute('disabled')) return;
        target.click();
      });
    });
  }

  /* ---- Gallery dots in the model strip ---- */
  function initGalleryDots() {
    document.querySelectorAll('[data-sat-gallery-dots]').forEach((dots) => {
      if (dots.dataset.ready) return;
      const gallery = document.getElementById(dots.dataset.gallery);
      if (!gallery) return;
      dots.dataset.ready = '1';
      const items = Array.from(dots.children);
      gallery.addEventListener('slideChanged', (e) => {
        const index = (e.detail?.currentPage || 1) - 1;
        items.forEach((d, i) => d.classList.toggle('is-active', i === index));
      });
    });
  }

  /* ---- Description clamp with VIEW MORE ---- */
  function initClamps() {
    document.querySelectorAll('[data-sat-clamp]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const inner = box.querySelector('.sat-clamp__inner');
      const toggle = box.querySelector('[data-sat-clamp-toggle]');
      const max = parseInt(getComputedStyle(box).getPropertyValue('--sat-clamp'), 10) || 0;
      if (!inner || !toggle || !max) return;
      const check = () => {
        if (box.classList.contains('is-clamped')) return;
        if (inner.scrollHeight > max + 30) {
          box.classList.add('is-clamped');
          toggle.hidden = false;
        }
      };
      check();
      box.closest('details')?.addEventListener('toggle', check);
      window.addEventListener('load', check, { once: true });
      toggle.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.querySelector('[data-more]').hidden = open;
        toggle.querySelector('[data-less]').hidden = !open;
      });
    });
  }

  /* ---- SHOW MORE on the collection grid ---- */
  if (!customElements.get('show-more-products')) {
    customElements.define(
      'show-more-products',
      class ShowMoreProducts extends HTMLElement {
        connectedCallback() {
          this.link = this.querySelector('[data-sat-showmore-link]');
          this.link?.addEventListener('click', this.onClick.bind(this));
        }
        async onClick(event) {
          const url = this.dataset.next;
          if (!url) return;
          event.preventDefault();
          this.classList.add('is-loading');
          this.link.setAttribute('aria-busy', 'true');
          try {
            const html = await (await fetch(url)).text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const grid = document.getElementById('product-grid');
            doc.querySelectorAll('#product-grid > li').forEach((li) => {
              li.classList.remove('scroll-trigger', 'animate--slide-in');
              li.removeAttribute('data-cascade');
              grid.appendChild(li);
            });
            const next = doc.querySelector('show-more-products');
            const count = this.querySelector('.sat-showmore__count');
            const newCount = next?.querySelector('.sat-showmore__count');
            if (count && newCount) count.textContent = newCount.textContent;
            if (next && next.dataset.next) {
              this.dataset.next = next.dataset.next;
              this.link.href = next.dataset.next;
            } else {
              this.dataset.next = '';
              this.link.remove();
            }
          } catch (e) {
            window.location.href = url;
          } finally {
            this.classList.remove('is-loading');
            this.link?.removeAttribute('aria-busy');
          }
        }
      }
    );
  }

  /* ---- Recommendations: drop the section when Shopify returns nothing (no padded gap) ---- */
  function initRecommendations() {
    document.querySelectorAll('product-recommendations').forEach((el) => {
      if (el.dataset.satWatched) return;
      el.dataset.satWatched = '1';
      const observer = new MutationObserver(() => {
        if (el.classList.contains('product-recommendations--loaded')) {
          observer.disconnect();
          return;
        }
        if (el.dataset.satSettled) return;
        el.dataset.satSettled = '1';
        setTimeout(() => {
          if (!el.querySelector('.grid__item')) el.closest('.shopify-section')?.remove();
        }, 150);
      });
      observer.observe(el, { childList: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ---- Klaviyo back in stock: inline capture shown while the chosen size is sold out ---- */
  function initBackInStock() {
    document.querySelectorAll('[data-sat-bis]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const target = document.getElementById(box.dataset.target);
      const form = box.querySelector('[data-sat-bis-form]');
      const msg = box.querySelector('[data-sat-bis-msg]');
      const submit = box.querySelector('.sat-bis__submit');
      if (!target || !form) return;
      const buttonsRow = target.closest('.product-form__buttons');
      const sync = () => {
        const soldOut = target.hasAttribute('disabled');
        box.hidden = !soldOut;
        /* The panel stands in for the dead Add to Bag, not under it (owner batch 2). */
        buttonsRow?.toggleAttribute('hidden', soldOut);
      };
      new MutationObserver(sync).observe(target, { attributes: true, attributeFilter: ['disabled'] });
      sync();
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const email = form.email.value.trim();
        const variant = document.getElementById(box.dataset.form)?.querySelector('[name="id"]')?.value;
        msg.hidden = true;
        msg.classList.remove('is-ok', 'is-error');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          msg.textContent = 'Enter a valid email address.';
          msg.classList.add('is-error');
          msg.hidden = false;
          return;
        }
        if (!variant) {
          msg.textContent = 'Pick a size first.';
          msg.classList.add('is-error');
          msg.hidden = false;
          return;
        }
        submit.setAttribute('aria-disabled', 'true');
        submit.classList.add('is-loading');
        try {
          const body =
            'a=' + encodeURIComponent(box.dataset.klaviyoKey) +
            '&email=' + encodeURIComponent(email) +
            '&variant=' + encodeURIComponent(variant) +
            '&platform=shopify';
          const res = await fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded', 'cache-control': 'no-cache' },
            body,
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.success !== false) {
            msg.textContent = "You're on the list. We'll email you the moment it's back.";
            msg.classList.add('is-ok');
            form.hidden = true;
          } else {
            msg.textContent = (data.errors && data.errors[0]) || 'Something went wrong. Try again.';
            msg.classList.add('is-error');
          }
        } catch (e) {
          msg.textContent = 'Network error. Try again.';
          msg.classList.add('is-error');
        } finally {
          msg.hidden = false;
          submit.removeAttribute('aria-disabled');
          submit.classList.remove('is-loading');
        }
      });
    });
  }

  function init() {
    initStickyAtc();
    initGalleryDots();
    initClamps();
    initRecommendations();
    initBackInStock();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);

  /* ---- Center the active category bubble in the rail (Moonless pattern) ---- */
  function centerActiveCatnav() {
    document.querySelectorAll('[data-sat-catnav]').forEach((rail) => {
      const active = rail.querySelector('.is-current');
      if (!active) return;
      const item = active.closest('.sat-catnav__item') || active;
      const target = item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
    });
  }
  if (document.readyState !== 'loading') centerActiveCatnav();
  else document.addEventListener('DOMContentLoaded', centerActiveCatnav);
  window.addEventListener('load', centerActiveCatnav);


  /* ---- Cart: pressing minus at qty 1 removes the line (no stuck 0 / "minimum of 1") ---- */
  document.addEventListener('click', function (event) {
    const minus = event.target.closest('.quantity__button[name="minus"]');
    if (!minus) return;
    /* only inside a cart context, never the PDP quantity selector */
    if (!minus.closest('cart-drawer, cart-items, .cart-drawer, .cart-items, .cart-item, .sat-cartitem__qty, #main-cart-items, .cart')) return;
    const qtyWrap = minus.closest('.quantity') || minus.parentElement;
    const input = qtyWrap && qtyWrap.querySelector('.quantity__input');
    if (!input) return;
    const val = parseInt(input.value, 10);
    const min = parseInt(input.getAttribute('data-min') || '1', 10);
    if (isNaN(val) || val > min) return; /* let the normal decrement run */
    event.preventDefault();
    event.stopImmediatePropagation();
    const idx = input.getAttribute('data-index');
    let removeBtn = idx && document.querySelector('cart-remove-button[data-index="' + idx + '"] button, .cart-remove-button[data-index="' + idx + '"]');
    if (!removeBtn) {
      const item = minus.closest('.cart-item, [class*="cart-item"], .sat-cartitem, li, tr');
      removeBtn = item && (item.querySelector('cart-remove-button button') || item.querySelector('.cart-remove-button'));
    }
    if (removeBtn) removeBtn.click();
  }, true);

})();
